interface EntityData {
  id: string;
  label: string;
  description: string;
  value?: string;
}

export interface TextEntity {
  key: string;
  data: EntityData;
  isCustom?: boolean;
}

/**
 * Extracts unique text entities from GIQ questions
 */
export function extractUniqueTextEntitiesFromGIQ(giqQuestions: any[]): TextEntity[] {
  if (!giqQuestions || giqQuestions.length === 0) {
    return [];
  }

  const uniqueTextEntities = new Map<string, TextEntity>();

  giqQuestions.forEach((question: any) => {
    if (question.textEntity && question.textEntity.textEntity) {
      const textEntityKey = question.textEntity.textEntity;
      if (!uniqueTextEntities.has(textEntityKey)) {
        uniqueTextEntities.set(textEntityKey, {
          key: textEntityKey,
          data: {
            id: question.textEntity.id,
            label: question.textEntity.textEntity,
            description: question.textEntity.description,
            value: question.answer || '',
          },
        });
      }
    }
  });

  return Array.from(uniqueTextEntities.values());
}

/**
 * Processes custom text entities from the API response
 */
export function processCustomTextEntities(customTextEntities: any[]): TextEntity[] {
  if (!customTextEntities || customTextEntities.length === 0) {
    return [];
  }

  return customTextEntities.map((entity: any) => ({
    key: entity.textEntity,
    data: {
      id: entity.id,
      label: entity.textEntity,
      description: entity.description,
      value: entity.substitutionValue,
    },
    isCustom: true,
  }));
}

/**
 * Processes content by replacing lexientity elements with their resolved values
 * @param content - The HTML content containing lexientity elements
 * @param textEntities - Array of text entities with their resolved values
 * @returns Processed content with lexientity elements replaced
 */
export function processLexientityContent(content: string, textEntities: TextEntity[]): string {
  if (!content || textEntities.length === 0) {
    return content;
  }

  if (!/<lexientity/i.test(content)) {
    return content;
  }

  const entityMap = new Map<string, string>();
  textEntities.forEach(entity => {
    entityMap.set(entity.data.label, entity.data.value || '');
  });

  const parser = new DOMParser();
  const doc = parser.parseFromString(content, 'text/html');
  const lexientityElements = doc.querySelectorAll('lexientity');

  lexientityElements.forEach((el) => {
    const textContent = el.textContent || '';
    const match = textContent.match(/\[([^\]]+)\]/);
    const entityLabel = match ? match[1].trim() : textContent.replace(/[\[\]]/g, '').trim();
    const resolvedValue = entityMap.get(entityLabel);
    let displayValue = resolvedValue ?? entityLabel;
    // Omit bracket content: strip leading [ and trailing ] from resolved display value
    if (displayValue.startsWith('[')) displayValue = displayValue.slice(1);
    if (displayValue.endsWith(']')) displayValue = displayValue.slice(0, -1);

    const span = doc.createElement('span');
    span.className = 'text-entity text-entity-resolved';
    span.textContent = displayValue;
    el.replaceWith(span);
  });

  return doc.body.innerHTML;
}

/**
 * Combines GIQ text entities and custom text entities into a single array
 */
export function combineTextEntities(giqTextEntities: TextEntity[], customTextEntities: TextEntity[]): TextEntity[] {
  const entityMap = new Map<string, TextEntity>();

  giqTextEntities.forEach((entity) => {
    if (entity.key) {
      entityMap.set(entity.key, entity);
    }
  });

  customTextEntities.forEach((entity) => {
    if (entity.key) {
      entityMap.set(entity.key, entity);
    }
  });

  return Array.from(entityMap.values());
}
